import {IconListTree} from '@tabler/icons-react';
import type {ReactNode} from 'react';
import {TreeResponseBrowser} from 'src/app/tree-browser/TreeResponseBrowser';
import {
  useGetComplianceStandardTreeQuery,
  useGetCustomComplianceStandardTreeQuery,
} from 'src/redux/queries/complianceStandard';
import {complianceTreeRootId} from './complianceStandardTreeState';

export interface ComplianceStandardTreeBrowserProps {
  variant: 'company' | 'regulatory';
  selectedId?: string;
  onSelect: (id: string) => void;
  activeOnly?: boolean;
  children: ReactNode;
}

// The tree endpoint reads only the type/active fields; paging and filters are ignored.
const treeRequest = {
  start: 0,
  length: 0,
  filters: [],
  filterFns: [],
  sorting: [],
};
const hierarchyIcon = <IconListTree size={16} />;

/** Company or Regulatory query adapter for the shared tree-response browser. */
export const ComplianceStandardTreeBrowser = ({
  variant,
  selectedId,
  onSelect,
  activeOnly,
  children,
}: ComplianceStandardTreeBrowserProps) => {
  const companyQuery = useGetCustomComplianceStandardTreeQuery(treeRequest, {
    skip: variant !== 'company',
  });
  const regulatoryQuery = useGetComplianceStandardTreeQuery(
    {...treeRequest, active: activeOnly},
    {skip: variant !== 'regulatory'}
  );
  const query = variant === 'company' ? companyQuery : regulatoryQuery;
  const rootLabel =
    variant === 'company'
      ? 'All Company Standards'
      : 'All Regulatory Standards';

  return (
    <TreeResponseBrowser
      query={query}
      rootId={complianceTreeRootId}
      rootLabel={rootLabel}
      paneTitle="Standard hierarchy"
      expansionKey={`compliance-${variant}`}
      selectedId={selectedId}
      onSelect={onSelect}
      icon={hierarchyIcon}
      searchPlaceholder="Search standard hierarchy"
      emptyText="No hierarchy values found."
      unavailableItemName="hierarchy selection"
      unavailableLabel="Unavailable hierarchy selection"
      resetLabel={`Show ${rootLabel}`}>
      {children}
    </TreeResponseBrowser>
  );
};
